import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { hotels as hotelsApi, rooms as roomsApi, bookings as bookingsApi, auth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { BACKEND_URL } from '@/lib/backendUrl';
import {
  Hotel, BedDouble, CalendarDays, DollarSign, Plus, Copy, ExternalLink,
  LogOut, Users, ShieldCheck, BarChart3, Settings, Phone, Sparkles,
  MapPin, Globe, Wallet, ArrowRight, CheckCircle, Menu, X, Ban, Calendar, Trash2, Download,
  LayoutGrid, ClipboardList, UserCheck, TrendingUp, LogIn, Bell, ChevronLeft, ChevronRight,
  Home, Clock, FileText, Wrench, Building2, CalendarCheck, Receipt, Headphones, FileSearch, Calculator, Send, BrainCircuit, UserCog, FileUp, MessageCircleQuestion, Brush, AlertTriangle, Tag, Sliders, Camera, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { FinancialSettings } from '@/components/FinancialSettings';
import NotificationCenter from '@/components/NotificationCenter';
import TelegramSetup from '@/components/TelegramSetup';
import { EnhancedStaffManagement } from '@/components/EnhancedStaffManagement';
import MCPIntegration from '@/components/MCPIntegration';
import AITermsConfig from '@/components/AITermsConfig';
import { WalletAddresses } from '@/components/WalletAddresses';
import { HotelSettings } from '@/components/HotelSettings';
import { HotelSettings as PmsHotelSettings } from '@/components/pms/HotelSettings';
import { RoomManager } from '@/components/RoomManager';
import { WidgetSettings } from '@/components/WidgetSettings';
import HotelBillingPage from '@/pages/HotelBillingPage';
import { PmsStatusBadge } from '@/components/pms/PmsStatusBadge';
import { PmsKpiCard } from '@/components/pms/PmsKpiCard';
import { PmsToolbar } from '@/components/pms/PmsToolbar';
import { EnhancedFrontDesk } from '@/components/EnhancedFrontDesk';
import { HotelVerificationPanel } from '@/components/verification/HotelVerificationPanel';
import { ReliabilitySettingsPanel } from '@/components/reliability/ReliabilitySettingsPanel';
import { CustomerSupportChat } from '@/components/pms/CustomerSupportChat';
import { SageChat } from '@/components/SageChat';

// Feature Tab Components
import { AvailabilityTab } from '@/features/availability/AvailabilityTab';
import { DataExportTab } from '@/features/export/DataExportTab';
import { FrontDeskTab } from '@/features/front-desk/FrontDeskTab';
import { TelegramTab } from '@/features/telegram/TelegramTab';
import { AiMcpTab } from '@/features/ai-config/AiMcpTab';
import { WidgetTab } from '@/features/widget/WidgetTab';
import { BillingTab } from '@/features/billing/BillingTab';
import { StaffTab } from '@/features/staff/StaffTab';
import { StaffManagementTab } from '@/features/pms/StaffManagementTab';
import { StaffQRManager } from '@/features/pms/StaffQRManager';
import { RequestsTab } from '@/features/requests/RequestsTab';
import { RoomBoardTab } from '@/features/pms/RoomBoardTab';
import { HousekeepingTab } from '@/features/pms/HousekeepingTab';
import { ReportsTab } from '@/features/reports/ReportsTab';
import { ModernOverviewTab } from '@/features/overview/OverviewTab';
import { MeetingRoomsTab } from '@/features/meeting-rooms/MeetingRoomsTab';
import { CryptoLedgerTab } from '@/features/crypto-ledger/CryptoLedgerTab';
import ChannelManagerTab from '@/features/channel-manager/ChannelManagerTab';
import RevenueComparisonTab from '@/features/channel-manager/RevenueComparisonTab';
import ReferralTab from '@/features/referral/ReferralTab';
import { CartIconButton, CartDrawer, useCartDrawer } from '@/features/cart';
import { UnifiedSettingsTab } from '@/features/settings/UnifiedSettingsTab';
import { GuestIssuesTab } from '@/features/complaints/GuestIssuesTab';
import { PricingDiscountsTab } from '@/features/pricing/PricingDiscountsTab';
import { PayrollTab } from '@/features/payroll/PayrollTab';
import { FeatureSettingsTab } from '@/features/feature-settings/FeatureSettingsTab';
import { BlockchainTab } from '@/features/blockchain/BlockchainTab';
import { PhotoManagementTab } from '@/features/photos/PhotoManagementTab';
import { filterNavItems, TAB_FEATURE_MAP } from '@/features/feature-settings/utils/featureGate';

const PREVIEW_URL = window.location.origin;

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DASHBOARD COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DASHBOARD COMPONENT - NEW SIDEBAR LAYOUT
// ─────────────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotelRooms, setHotelRooms] = useState([]);
  const [hotelBookings, setHotelBookings] = useState([]);
  const [hotelFeatures, setHotelFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sageOpen, setSageOpen] = useState(false);
  
  // Cart drawer state
  const { isOpen: cartOpen, toggleCart, setIsOpen: setCartOpen } = useCartDrawer();

  // Listen for cart open events from toast actions
  useEffect(() => {
    const handleOpenCart = () => setCartOpen(true);
    window.addEventListener('openCartDrawer', handleOpenCart);
    return () => window.removeEventListener('openCartDrawer', handleOpenCart);
  }, [setCartOpen]);

  useEffect(() => {
    auth.me()
      .then(({ data }) => {
        setUser(data);
        loadHotels(data.id);
      })
      .catch(() => {
        toast.error('Please log in');
        navigate('/login');
      });
  }, [navigate]);

  const loadHotels = async (userId) => {
    try {
      const { data } = await hotelsApi.list(userId);
      setHotels(data);
      if (data.length > 0) {
        setSelectedHotel(data[0]);
        loadHotelData(data[0].id);
      }
      setLoading(false);
    } catch (e) {
      toast.error('Failed to load hotels');
      setLoading(false);
    }
  };

  const loadHotelData = async (hotelId) => {
    try {
      const [roomsRes, bookingsRes] = await Promise.all([
        roomsApi.list(hotelId),
        bookingsApi.listForHotel(hotelId)
      ]);
      setHotelRooms(roomsRes.data);
      setHotelBookings(bookingsRes.data);
      
      // Load hotel features for navigation gating
      loadHotelFeatures(hotelId);
    } catch (e) {
      toast.error('Failed to load hotel data');
    }
  };

  const loadHotelFeatures = async (hotelId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/features/${hotelId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('easystay_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHotelFeatures(data.config);
      }
    } catch (e) {
      // Silently fail - features will default to showing all tabs
      console.warn('Failed to load hotel features for navigation gating');
    }
  };

  const handlePropertySwitch = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    if (hotel) {
      setSelectedHotel(hotel);
      loadHotelData(hotelId);
      setActiveSection('overview'); // Reset to overview when switching properties
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('easystay_token');
    navigate('/login');
  };

  const copyBookingLink = async () => {
    if (selectedHotel) {
      const link = `${PREVIEW_URL}/book/${selectedHotel.id}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success('Booking link copied to clipboard!');
      } catch (err) {
        toast.info('Link: ' + link, {
          duration: 10000,
          description: 'Copy this link manually (clipboard blocked in preview)'
        });
      }
    }
  };

  // Navigation items matching design guidelines
  const navItems = [
    { id: 'overview', label: 'Overview', icon: FileSearch, testId: 'owner-nav-overview-link' },
    { id: 'photos', label: 'Photos', icon: Camera, testId: 'owner-nav-photos-link' },
    { id: 'front-desk', label: 'Front Desk', icon: Headphones, testId: 'owner-nav-front-desk-link' },
    { id: 'guest-issues', label: 'Guest Issues', icon: AlertTriangle, testId: 'owner-nav-guest-issues-link' },
    { id: 'rooms', label: 'Room Management', icon: BedDouble, testId: 'owner-nav-rooms-link' },
    { id: 'bookings', label: 'Bookings', icon: CalendarDays, testId: 'owner-nav-bookings-link' },
    { id: 'meeting-rooms', label: 'Meeting Rooms', icon: Users, testId: 'owner-nav-meeting-rooms-link' },
    { id: 'room-board', label: 'Room Board', icon: LayoutGrid, testId: 'owner-nav-room-board-link' },
    { id: 'housekeeping', label: 'Housekeeping', icon: Sparkles, testId: 'owner-nav-housekeeping-link' },
    { id: 'requests', label: 'Requests', icon: MessageCircleQuestion, testId: 'owner-nav-requests-link' },
    { id: 'staff', label: 'Staff', icon: UserCog, testId: 'owner-nav-staff-link' },
    { id: 'staff-qr', label: 'Staff QR Codes', icon: QrCode, testId: 'owner-nav-staff-qr-link' },
    { id: 'payroll', label: 'Payroll', icon: DollarSign, testId: 'owner-nav-payroll-link' },
    { id: 'availability', label: 'Availability', icon: CalendarCheck, testId: 'owner-nav-availability-link' },
    { id: 'pricing-discounts', label: 'Pricing & Discounts', icon: Tag, testId: 'owner-nav-pricing-link' },
    { id: 'reports', label: 'Reports', icon: BarChart3, testId: 'owner-nav-reports-link' },
    { id: 'billing', label: 'Finance & Billing', icon: Calculator, testId: 'owner-nav-billing-link' },
    { id: 'crypto-ledger', label: 'Crypto Ledger', icon: Receipt, testId: 'owner-nav-crypto-ledger-link' },
    { id: 'channel-manager', label: 'Channel Manager', icon: Globe, testId: 'owner-nav-channel-manager-link' },
    { id: 'revenue-compare', label: 'Revenue Comparison', icon: TrendingUp, testId: 'owner-nav-revenue-compare-link' },
    { id: 'referrals', label: 'Referral Program', icon: Users, testId: 'owner-nav-referrals-link' },
    { id: 'telegram', label: 'Telegram', icon: Send, testId: 'owner-nav-telegram-link' },
    { id: 'ai-mcp', label: 'AI & MCP', icon: BrainCircuit, testId: 'owner-nav-ai-mcp-link' },
    { id: 'widget', label: 'Widget', icon: Globe, testId: 'owner-nav-widget-link' },
    { id: 'feature-settings', label: 'Feature Settings', icon: Sliders, testId: 'owner-nav-feature-settings-link' },
    { id: 'blockchain', label: 'Blockchain Dashboard', icon: Wallet, testId: 'owner-nav-blockchain-link' },
    { id: 'settings', label: 'Settings', icon: Settings, testId: 'owner-nav-settings-link' },
    { id: 'data-export', label: 'Export Data', icon: FileUp, testId: 'owner-nav-export-link' }
  ];

  const renderActiveContent = () => {
    if (!selectedHotel) return null;

    // Deep-link guard: Check if current section is disabled
    if (hotelFeatures && !filterNavItems([{ id: activeSection }], hotelFeatures).length) {
      const requiredFeatures = TAB_FEATURE_MAP[activeSection] || [];
      const featureNames = requiredFeatures.map(f => f.replace(/_/g, ' ').replace('enabled', '')).join(', ');
      
      return (
        <div className="space-y-6" data-testid="disabled-section-guard">
          <div>
            <h2 className="text-2xl font-bold heading-font mb-2">Feature Disabled</h2>
            <p className="text-muted-foreground">
              This section is currently unavailable
            </p>
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-2">
                <strong>{navItems.find(item => item.id === activeSection)?.label || 'This section'}</strong> requires the following features to be enabled:
              </p>
              <p className="text-sm mb-4">
                {featureNames || 'Required features'}
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => setActiveSection('feature-settings')}
                  data-testid="go-to-feature-settings"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Enable in Feature Settings
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setActiveSection('overview')}
                  data-testid="go-to-overview"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Overview
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    switch (activeSection) {
      case 'overview':
        return <ModernOverviewTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} hotels={hotels} />;
      
      case 'photos':
        return <PhotoManagementTab hotelId={selectedHotel.id} />;
      
      case 'front-desk':
        return <FrontDeskTab hotelId={selectedHotel.id} />;
      
      case 'guest-issues':
        return <GuestIssuesTab hotelId={selectedHotel.id} currentUser={user} />;
      
      case 'rooms':
        return (
          <div className="space-y-6" data-testid="owner-rooms-content">
            <div>
              <h2 className="text-2xl font-bold heading-font mb-2">Room Management</h2>
              <p className="text-muted-foreground">Manage your room inventory, pricing, and availability</p>
            </div>
            
            {/* Room Manager Component - Has its own Add Room button and displays all rooms */}
            <RoomManager hotelId={selectedHotel.id} />
          </div>
        );
      
      case 'bookings':
        return (
          <div className="space-y-6" data-testid="owner-bookings-content">
            <div>
              <h2 className="text-2xl font-bold heading-font mb-2">All Bookings</h2>
              <p className="text-muted-foreground">View and manage all reservations</p>
            </div>
            <Card>
              <CardContent className="overflow-x-auto p-0">
                {!Array.isArray(hotelBookings) || hotelBookings.length === 0 ? (
                  <div className="p-8 text-center">
                    <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                    <p className="text-center text-muted-foreground">No bookings yet. Share your booking link to get started!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Guest</TableHead>
                        <TableHead className="hidden md:table-cell">Email</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead className="hidden sm:table-cell">Guests</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Check-in</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Array.isArray(hotelBookings) && hotelBookings.map(b => (
                        <TableRow key={b.id} className={b.checked_in ? 'bg-green-50 dark:bg-green-950/20' : ''} data-testid={`booking-row-${b.id}`}>
                          <TableCell className="font-medium">
                            {b.guest_name}
                            {b.is_walk_in && <Badge variant="outline" className="ml-2 text-xs">Walk-in</Badge>}
                          </TableCell>
                          <TableCell className="text-sm hidden md:table-cell">{b.guest_email}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap mono">{b.check_in} → {b.check_out}</TableCell>
                          <TableCell className="hidden sm:table-cell">{b.guests}</TableCell>
                          <TableCell className="mono font-semibold">${b.final_price}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{b.payment_method}</Badge></TableCell>
                          <TableCell><Badge variant={b.booking_status === 'confirmed' ? 'default' : 'secondary'}>{b.booking_status}</Badge></TableCell>
                          <TableCell>
                            {b.checked_in ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Checked In
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Pending</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        );
      
      case 'staff':
        return <StaffManagementTab hotelId={selectedHotel.id} />;
      
      case 'staff-qr':
        return <StaffQRManager hotelId={selectedHotel.id} />;
      
      case 'payroll':
        return <PayrollTab user={user} selectedHotelId={selectedHotel.id} />;
      
      case 'availability':
        return <AvailabilityTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} />;
      
      case 'requests':
        return <RequestsTab hotelId={selectedHotel.id} />;
      
      case 'room-board':
        return <RoomBoardTab hotelId={selectedHotel.id} />;
      
      case 'housekeeping':
        return <HousekeepingTab hotelId={selectedHotel.id} />;
      
      case 'pricing-discounts':
        return <PricingDiscountsTab hotelId={selectedHotel.id} />;
      
      case 'reports':
        return <ReportsTab hotelId={selectedHotel.id} />;
      
      case 'billing':
        return <BillingTab hotelId={selectedHotel.id} />;
      
      case 'crypto-ledger':
        return <CryptoLedgerTab hotelId={selectedHotel.id} />;
      
      case 'channel-manager':
        return <ChannelManagerTab hotelId={selectedHotel.id} />;
      
      case 'revenue-compare':
        return <RevenueComparisonTab hotelId={selectedHotel.id} />;
      
      case 'referrals':
        return <ReferralTab hotelId={selectedHotel.id} />;
      
      case 'telegram':
        return <TelegramTab hotelId={selectedHotel.id} />;
      
      case 'ai-mcp':
        return <AiMcpTab hotelId={selectedHotel.id} />;
      
      case 'widget':
        return <WidgetTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} />;
      
      case 'meeting-rooms':
        return <MeetingRoomsTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} currentUser={user} />;
      
      case 'feature-settings':
        return <FeatureSettingsTab hotelId={selectedHotel.id} currentUser={user} />;
      
      case 'blockchain':
        return <BlockchainTab hotelId={selectedHotel.id} />;
      
      case 'settings':
        return <UnifiedSettingsTab hotelId={selectedHotel.id} hotel={selectedHotel} user={user} onHotelUpdate={(updated) => setSelectedHotel(updated)} />;
      
      case 'data-export':
        return <DataExportTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} />;
      
      default:
        return <ModernOverviewTab hotelId={selectedHotel.id} hotelName={selectedHotel.name} hotels={hotels} />;
    }
  };

  if (loading) {
    return (
      <div className="lobby-theme flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  // Sidebar navigation component
  const SidebarNav = ({ isMobile = false }) => (
    <div className="flex flex-col h-full" data-testid="owner-sidebar">
      {/* Property Switcher */}
      <div className="p-4 border-b border-[color:var(--l-line)]" data-testid="owner-sidebar-property-switcher">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-[color:var(--l-copper)]/15 border border-[color:var(--l-copper)]/30 flex items-center justify-center flex-shrink-0">
            <Hotel className="w-5 h-5 text-[color:var(--l-copper)]" />
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--l-cream-3)]">Property</p>
              <p className="l-display text-[14px] truncate mt-0.5">{selectedHotel?.name || 'Select Property'}</p>
            </div>
          )}
        </div>
        {!sidebarCollapsed && hotels.length > 1 && (
          <Select value={selectedHotel?.id} onValueChange={handlePropertySwitch}>
            <SelectTrigger className="w-full" data-testid="property-switcher-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {hotels.map(hotel => (
                <SelectItem key={hotel.id} value={hotel.id} data-testid={`property-switcher-option-${hotel.id}`}>
                  <div className="flex items-center gap-2">
                    <Hotel className="w-4 h-4" />
                    <span>{hotel.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Removed CreateHotelWizard - not implemented yet */}
        {/* {!sidebarCollapsed && hotels.length === 1 && (
          <div className="mt-2">
            <CreateHotelWizard onCreated={() => loadHotels(user?.id)} />
          </div>
        )} */}
      </div>

      {/* Navigation Items */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {filterNavItems(navItems, hotelFeatures)
            .filter(item => {
              // Filter Feature Settings - Owner/Manager only
              if (item.id === 'feature-settings') {
                return user && (user.role === 'owner' || user.role === 'manager');
              }
              return true;
            })
            .map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <TooltipProvider key={item.id} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        setActiveSection(item.id);
                        if (isMobile) setMobileMenuOpen(false);
                      }}
                      data-testid={item.testId}
                      className={`
                        group flex items-center gap-3 rounded-lg px-3 py-2 text-sm w-full
                        transition-colors duration-200
                        ${isActive 
                          ? 'bg-secondary text-foreground ring-1 ring-border relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:rounded-full before:bg-primary' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </button>
                  </TooltipTrigger>
                  {sidebarCollapsed && (
                    <TooltipContent side="right">
                      <p className="font-semibold">{item.label}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </nav>
      </ScrollArea>

      {/* User Info & Logout */}
      <div className="p-4 border-t">
        {!sidebarCollapsed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">{user?.name?.charAt(0) || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || 'Hotel Owner'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="w-full"
              data-testid="owner-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        ) : (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleLogout}
                  className="w-full"
                  data-testid="owner-logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-semibold">Log Out</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="lobby-theme flex h-screen overflow-hidden" data-testid="owner-dashboard">
        {/* Desktop Sidebar */}
        <aside
          className={`
            hidden lg:flex flex-col border-r border-[color:var(--l-line)] bg-[color:var(--l-bg-2)]
            transition-all duration-300 ease-in-out
            ${sidebarCollapsed ? 'w-[76px]' : 'w-[280px]'}
          `}
        >
          <SidebarNav />
        </aside>

        {/* Mobile Menu */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-[280px] p-0 bg-[color:var(--l-bg-2)] border-[color:var(--l-line)]">
            <SidebarNav isMobile />
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Top Header */}
          <header className="border-b border-[color:var(--l-line)] bg-[color:var(--l-bg)]/90 backdrop-blur-md px-4 sm:px-6 lg:px-8 py-4 relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Mobile Menu Button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setMobileMenuOpen(true)}
                  className="lg:hidden flex-shrink-0 border-[color:var(--l-line-2)]"
                  data-testid="mobile-menu-button"
                  aria-label="Open navigation menu"
                >
                  <Menu className="w-5 h-5" />
                </Button>

                {/* Sidebar Collapse Toggle (Desktop Only) */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="hidden lg:flex text-[color:var(--l-cream-2)] hover:text-[color:var(--l-cream)]"
                  data-testid="sidebar-collapse-toggle"
                >
                  {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
                </Button>

                {/* Page Title */}
                <div>
                  <h1 className="l-display text-2xl sm:text-[28px] tracking-tight" data-testid="page-title">
                    {navItems.find(item => item.id === activeSection)?.label || 'Overview'}
                  </h1>
                  <p className="text-[13px] text-[color:var(--l-cream-3)] hidden sm:block mt-0.5">
                    {selectedHotel?.name}
                  </p>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-3">
                <CartIconButton onClick={toggleCart} />
                <NotificationCenter hotelId={selectedHotel?.id} />
                <div className="lg:hidden">
                  <div className="w-9 h-9 rounded-full bg-[color:var(--l-copper)]/15 border border-[color:var(--l-copper)]/30 flex items-center justify-center">
                    <span className="text-xs font-bold text-[color:var(--l-copper)]">{user?.name?.charAt(0) || 'U'}</span>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px]">
              {selectedHotel ? (
                renderActiveContent()
              ) : (
                <div className="flex items-center justify-center h-full p-8">
                  <div className="text-center max-w-md lobby-reveal">
                    <div className="w-16 h-16 rounded-full bg-[color:var(--l-copper)]/15 border border-[color:var(--l-copper)]/30 flex items-center justify-center mx-auto mb-6">
                      <Hotel className="w-7 h-7 text-[color:var(--l-copper)]" />
                    </div>
                    <h2 className="l-display text-3xl mb-3">
                      Welcome to <span className="text-[color:var(--l-copper)]">getbitsy</span>.
                    </h2>
                    <p className="text-[color:var(--l-cream-2)] mb-8 leading-relaxed">
                      Create your first hotel to start accepting AI-powered bookings and settle payments the way your guests prefer.
                    </p>
                    <button onClick={() => loadHotels(user?.id)} className="l-btn-copper h-11 px-6 text-sm inline-flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Get started
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Sage AI Chat (only for admins and managers) */}
        {user && selectedHotel && (user.role === 'admin' || user.role === 'manager' || user.role === 'owner') && (
          <SageChat
            hotelId={selectedHotel.id}
            currentUser={user}
            isOpen={sageOpen}
            onToggle={() => setSageOpen(!sageOpen)}
          />
        )}

        {/* Cart Drawer */}
        <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
      </div>
    </TooltipProvider>
  );
}
